/**
 * US-100: command-palette ⌘K.
 *
 * Быстрый прыжок без мыши:
 *  - «Разделы» — навигация по пунктам меню (фильтр по подстроке; пустой
 *    запрос показывает все доступные разделы).
 *  - «Перейти к неделе» — если ввод парсится как дата (17.08.2026 / 2026-08-17),
 *    предлагает открыть таймшит недели (понедельник этой даты). Скрыто у
 *    exempt-сотрудников (таймшит не ведут).
 *  - «Сотрудники» — поиск по людям в зоне видимости (debounce 200мс,
 *    GET /api/employees/search/). Рядовой сотрудник видит только себя —
 *    поэтому полезно прежде всего руководителю. Выбор → история сотрудника.
 *
 * Клавиши: ↑/↓ — выбор, Enter — открыть, Esc — закрыть (Mantine Modal).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Group, Loader, Modal, ScrollArea, Text, TextInput } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Calendar, CornerDownLeft, Search, User as UserIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { EmployeeSearchResult, Me } from "../api/client";
import { fetchEmployeeSearch } from "../api/client";
import { parseRuDate } from "./AbsenceForm";

export interface PaletteSection {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
}

interface PaletteAction {
  key: string;
  group: string;
  label: string;
  sub?: string;
  icon: LucideIcon;
  run: () => void;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  me: Me;
  sections: PaletteSection[];
}

/** Понедельник недели, в которую попадает дата (без isoWeek-плагина). */
function mondayOf(date: Date): dayjs.Dayjs {
  const d = dayjs(date);
  const dow = (d.day() + 6) % 7; // Пн=0 … Вс=6
  return d.subtract(dow, "day").startOf("day");
}

export function CommandPalette({ opened, onClose, me, sections }: Props) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<EmployeeSearchResult[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const reqIdRef = useRef(0);

  // Сброс при каждом открытии.
  useEffect(() => {
    if (opened) {
      setQuery("");
      setPeople([]);
      setLoadingPeople(false);
      setActiveIndex(0);
    }
  }, [opened]);

  // Поиск по людям — debounce + защита от гонки устаревших ответов.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setPeople([]);
      setLoadingPeople(false);
      return;
    }
    setLoadingPeople(true);
    const myReq = ++reqIdRef.current;
    const t = setTimeout(() => {
      fetchEmployeeSearch(q)
        .then((rows) => {
          if (myReq === reqIdRef.current) setPeople(rows);
        })
        .catch(() => {
          if (myReq === reqIdRef.current) setPeople([]);
        })
        .finally(() => {
          if (myReq === reqIdRef.current) setLoadingPeople(false);
        });
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const actions = useMemo<PaletteAction[]>(() => {
    const q = query.trim().toLowerCase();
    const list: PaletteAction[] = [];

    for (const s of sections) {
      if (!q || s.label.toLowerCase().includes(q)) {
        list.push({
          key: `sec-${s.id}`,
          group: "Разделы",
          label: s.label,
          icon: s.icon,
          run: () => {
            navigate(s.path);
            onClose();
          },
        });
      }
    }

    if (!me.timesheet_exempt && query.trim()) {
      const parsed = parseRuDate(query.trim());
      if (parsed) {
        const monday = mondayOf(parsed);
        list.push({
          key: "week",
          group: "Перейти к неделе",
          label: `Неделя с ${monday.format("DD.MM.YYYY")}`,
          sub: "Открыть таймшит этой недели",
          icon: Calendar,
          run: () => {
            navigate(`/timesheet/${monday.format("YYYY-MM-DD")}`);
            onClose();
          },
        });
      }
    }

    for (const p of people) {
      list.push({
        key: `emp-${p.id}`,
        group: "Сотрудники",
        label: p.full_name,
        sub: p.position || undefined,
        icon: UserIcon,
        run: () => {
          navigate(`/history?employee=${p.id}`);
          onClose();
        },
      });
    }

    return list;
  }, [query, sections, people, me.timesheet_exempt, navigate, onClose]);

  // Активный индекс не должен выходить за пределы текущего списка.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, people]);
  const clampedIndex = actions.length ? Math.min(activeIndex, actions.length - 1) : 0;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, actions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      actions[clampedIndex]?.run();
    }
  };

  let lastGroup = "";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="lg"
      fullScreen={isMobile}
      padding={0}
      yOffset={isMobile ? 0 : "12vh"}
      overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
      data-testid="command-palette"
    >
      <Box p="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <TextInput
          autoFocus
          data-autofocus
          data-testid="command-palette-input"
          variant="unstyled"
          size="md"
          placeholder="Разделы, неделя (17.08.2026), сотрудник…"
          leftSection={<Search size={16} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
        />
      </Box>

      <ScrollArea.Autosize mah={360}>
        <Box p={6}>
          {actions.length === 0 && (
            <Group justify="center" py="lg" gap={8}>
              {loadingPeople ? (
                <Loader size="xs" />
              ) : (
                <Text size="sm" c="dimmed">
                  Ничего не найдено
                </Text>
              )}
            </Group>
          )}

          {actions.map((a, idx) => {
            const showHeader = a.group !== lastGroup;
            lastGroup = a.group;
            const active = idx === clampedIndex;
            const Icon = a.icon;
            return (
              <Box key={a.key}>
                {showHeader && (
                  <Text
                    size="xs"
                    c="dimmed"
                    fw={600}
                    tt="uppercase"
                    px={8}
                    pt={idx === 0 ? 2 : 10}
                    pb={4}
                    style={{ letterSpacing: "0.04em" }}
                  >
                    {a.group}
                  </Text>
                )}
                <Group
                  data-testid={`palette-action-${a.key}`}
                  data-active={active || undefined}
                  gap={10}
                  wrap="nowrap"
                  px={8}
                  py={7}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => a.run()}
                  style={{
                    cursor: "pointer",
                    borderRadius: 6,
                    background: active ? "var(--mantine-color-default-hover)" : "transparent",
                  }}
                >
                  <Icon size={15} style={{ flexShrink: 0, opacity: 0.7 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" truncate>
                      {a.label}
                    </Text>
                    {a.sub && (
                      <Text size="xs" c="dimmed" truncate>
                        {a.sub}
                      </Text>
                    )}
                  </Box>
                  {active && <CornerDownLeft size={13} style={{ opacity: 0.5 }} />}
                </Group>
              </Box>
            );
          })}
        </Box>
      </ScrollArea.Autosize>
    </Modal>
  );
}
